import React, { useState } from "react";
import { useSelector } from "react-redux";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Fade from "@mui/material/Fade";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import CalendarTodayRoundedIcon from "@mui/icons-material/CalendarTodayRounded";
import PeopleRoundedIcon from "@mui/icons-material/PeopleRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import BlockRoundedIcon from "@mui/icons-material/BlockRounded";
import { Localize } from "../../../app/lang/langs";
import { useNavigate } from "react-router-dom";

export default function VcompManageNotification() {
    const navigate = useNavigate();
    const vcompManageNotification = useSelector(
        (state) => state.dashboard.vcompManageNotification
    );
    const selectedTeamsIds = useSelector((state) => state.teams.selectedTeamsIds);
    const [expandedRows, setExpandedRows] = useState({});

    const navToVcompProject = (rowData) => {
        const encodedLabel = encodeURIComponent(rowData.project_name);
        navigate(`/vcomp/${selectedTeamsIds[0]}/1/${encodedLabel}`);
    };

    const toggleExpand = (index) => {
        setExpandedRows((prev) => ({
            ...prev,
            [index]: !prev[index],
        }));
    };

    if (!Array.isArray(vcompManageNotification) || vcompManageNotification.length === 0) {
        return (
            <Typography
                variant="body2"
                sx={{
                    textAlign: "center",
                    py: 3,
                    color: "rgba(0, 40, 128, 0.4)",
                    fontStyle: "italic",
                    fontSize: "0.9rem",
                }}
            >
                {Localize("NO_NOTIFICATIONS") || "Aucune notification"}
            </Typography>
        );
    }

    return (
        <Stack spacing={1.5}>
            {vcompManageNotification.map((row, index) => {
                const collabs = row.collabs || [];
                const hasCollabs = collabs.length > 0;
                const isExpanded = !!expandedRows[index];
                const hasUnanswered = row.unansweredCollabs > 0;

                return (
                    <Fade in timeout={300 + index * 80} key={row.project_name + "-" + index}>
                        <Card
                            elevation={0}
                            sx={{
                                borderRadius: "14px",
                                border: "1px solid rgba(227, 227, 227, 0.45)",
                                background: "rgba(255, 255, 255, 0.6)",
                                transition: "all 220ms cubic-bezier(0.4, 0, 0.2, 1)",
                                overflow: "hidden",
                            }}
                        >
                            <Box sx={{ px: 2, py: 1.5 }}>
                                {/* Top row: project name (clickable) + session badge */}
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    sx={{ mb: 1 }}
                                >
                                    <Typography
                                        variant="subtitle2"
                                        onClick={() => navToVcompProject(row)}
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: "0.9rem",
                                            color: "rgb(8, 137, 193)",
                                            lineHeight: 1.3,
                                            flex: 1,
                                            mr: 1,
                                            cursor: "pointer",
                                            transition: "all 200ms cubic-bezier(0.4, 0, 0.2, 1)",
                                            "&:hover": {
                                                color: "rgb(6, 115, 165)",
                                                textShadow: "0 0 12px rgba(8, 137, 193, 0.15)",
                                            },
                                        }}
                                    >
                                        {row.project_name}
                                    </Typography>
                                    <Chip
                                        label={`S${row.session_number ?? "-"}`}
                                        size="small"
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: "0.75rem",
                                            height: 24,
                                            bgcolor: "rgba(100, 116, 139, 0.1)",
                                            color: "rgba(51, 65, 85, 0.8)",
                                            borderRadius: "8px",
                                        }}
                                    />
                                </Stack>

                                {/* Date row */}
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={0.5}
                                    sx={{ mb: 1.5 }}
                                >
                                    <CalendarTodayRoundedIcon
                                        sx={{
                                            fontSize: 14,
                                            color: "rgba(255, 144, 21, 0.7)",
                                        }}
                                    />
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: "rgba(0, 40, 128, 0.6)",
                                            fontSize: "0.8rem",
                                        }}
                                    >
                                        {row.start_date_raw || "-"}
                                    </Typography>
                                </Stack>

                                {/* Stats row: total collabs + unanswered */}
                                <Stack
                                    direction="row"
                                    spacing={1.5}
                                    alignItems="center"
                                    sx={{ mb: hasCollabs ? 1 : 0 }}
                                >
                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                        <PeopleRoundedIcon
                                            sx={{
                                                fontSize: 16,
                                                color: "rgba(100, 116, 139, 0.6)",
                                            }}
                                        />
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                fontWeight: 600,
                                                fontSize: "0.8rem",
                                                color: "rgba(51, 65, 85, 0.8)",
                                            }}
                                        >
                                            {row.totalCollabs ?? 0}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                fontSize: "0.75rem",
                                                color: "rgba(100, 116, 139, 0.6)",
                                            }}
                                        >
                                            {Localize("TOTAL_COLLABS") || "total"}
                                        </Typography>
                                    </Stack>

                                    {hasUnanswered && (
                                        <Stack direction="row" alignItems="center" spacing={0.5}>
                                            <WarningAmberRoundedIcon
                                                sx={{
                                                    fontSize: 16,
                                                    color: "rgb(217, 119, 6)",
                                                }}
                                            />
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    fontWeight: 600,
                                                    fontSize: "0.8rem",
                                                    color: "rgb(217, 119, 6)",
                                                }}
                                            >
                                                {row.unansweredCollabs}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    fontSize: "0.75rem",
                                                    color: "rgb(217, 119, 6)",
                                                    opacity: 0.8,
                                                }}
                                            >
                                                {Localize("UNASWERED_COLLABS") || "sans réponse"}
                                            </Typography>
                                        </Stack>
                                    )}

                                    {/* Expand toggle */}
                                    {hasCollabs && (
                                        <Box sx={{ ml: "auto !important" }}>
                                            <IconButton
                                                size="small"
                                                onClick={() => toggleExpand(index)}
                                                sx={{
                                                    width: 32,
                                                    height: 32,
                                                    color: "rgba(255, 144, 21, 0.7)",
                                                    transition: "all 200ms ease",
                                                    transform: isExpanded
                                                        ? "rotate(180deg)"
                                                        : "rotate(0deg)",
                                                    "&:hover": {
                                                        bgcolor: "rgba(255, 144, 21, 0.1)",
                                                        color: "rgba(255, 144, 21, 1)",
                                                    },
                                                }}
                                            >
                                                <ExpandMoreRoundedIcon sx={{ fontSize: 22 }} />
                                            </IconButton>
                                        </Box>
                                    )}
                                </Stack>
                            </Box>

                            {/* Expandable collabs list */}
                            {hasCollabs && (
                                <Collapse in={isExpanded} timeout={250}>
                                    <Divider
                                        sx={{
                                            borderColor: "rgba(255, 144, 21, 0.1)",
                                        }}
                                    />
                                    <Box
                                        sx={{
                                            bgcolor: "rgba(245, 241, 234, 0.35)",
                                            maxHeight: 240,
                                            overflowY: "auto",
                                            "&::-webkit-scrollbar": {
                                                width: 4,
                                            },
                                            "&::-webkit-scrollbar-thumb": {
                                                bgcolor: "rgba(255, 144, 21, 0.22)",
                                                borderRadius: 4,
                                            },
                                        }}
                                    >
                                        <List disablePadding dense>
                                            {collabs.map((collab, cIdx) => {
                                                const answered = !!collab.answered;
                                                const isActiveMcq =
                                                    collab.active_mcq === "1" ||
                                                    collab.active_mcq === 1 ||
                                                    collab.active_mcq === true;

                                                return (
                                                    <React.Fragment
                                                        key={`${collab.prenom}-${collab.nom}-${cIdx}`}
                                                    >
                                                        <ListItem
                                                            sx={{
                                                                py: 1,
                                                                px: 2,
                                                                minHeight: 52,
                                                            }}
                                                        >
                                                            <ListItemText
                                                                primary={`${collab.prenom} ${collab.nom}`}
                                                                primaryTypographyProps={{
                                                                    variant: "body2",
                                                                    fontWeight: 500,
                                                                    fontSize: "0.85rem",
                                                                    color: "rgba(30, 30, 30, 0.82)",
                                                                }}
                                                            />
                                                            <Stack
                                                                direction="row"
                                                                spacing={0.75}
                                                                alignItems="center"
                                                                sx={{ flexShrink: 0, ml: 1 }}
                                                            >
                                                                {/* Answered chip */}
                                                                <Chip
                                                                    icon={
                                                                        answered ? (
                                                                            <CheckCircleRoundedIcon
                                                                                sx={{
                                                                                    fontSize: 14,
                                                                                }}
                                                                            />
                                                                        ) : (
                                                                            <CancelRoundedIcon
                                                                                sx={{
                                                                                    fontSize: 14,
                                                                                }}
                                                                            />
                                                                        )
                                                                    }
                                                                    label={
                                                                        answered
                                                                            ? Localize("YES") ||
                                                                              "Oui"
                                                                            : Localize("NO") ||
                                                                              "Non"
                                                                    }
                                                                    size="small"
                                                                    sx={{
                                                                        fontWeight: 500,
                                                                        fontSize: "0.72rem",
                                                                        height: 24,
                                                                        borderRadius: "16px",
                                                                        bgcolor: answered
                                                                            ? "rgba(34, 197, 94, 0.1)"
                                                                            : "rgba(239, 68, 68, 0.1)",
                                                                        color: answered
                                                                            ? "rgb(22, 163, 74)"
                                                                            : "rgb(220, 38, 38)",
                                                                        "& .MuiChip-icon": {
                                                                            color: "inherit",
                                                                        },
                                                                    }}
                                                                />
                                                                {/* MCQ status chip */}
                                                                <Chip
                                                                    icon={
                                                                        isActiveMcq ? (
                                                                            <AccessTimeRoundedIcon
                                                                                sx={{
                                                                                    fontSize: 13,
                                                                                }}
                                                                            />
                                                                        ) : (
                                                                            <BlockRoundedIcon
                                                                                sx={{
                                                                                    fontSize: 13,
                                                                                }}
                                                                            />
                                                                        )
                                                                    }
                                                                    label={
                                                                        isActiveMcq
                                                                            ? Localize(
                                                                                  "MCQ_PENDING"
                                                                              ) || "En attente"
                                                                            : Localize(
                                                                                  "MCQ_DISABLED"
                                                                              ) || "QCM désactivé"
                                                                    }
                                                                    size="small"
                                                                    sx={{
                                                                        fontWeight: 500,
                                                                        fontSize: "0.72rem",
                                                                        height: 24,
                                                                        borderRadius: "16px",
                                                                        bgcolor: isActiveMcq
                                                                            ? "rgba(245, 158, 11, 0.1)"
                                                                            : "rgba(100, 116, 139, 0.08)",
                                                                        color: isActiveMcq
                                                                            ? "rgb(217, 119, 6)"
                                                                            : "rgba(100, 116, 139, 0.6)",
                                                                        "& .MuiChip-icon": {
                                                                            color: "inherit",
                                                                        },
                                                                    }}
                                                                />
                                                            </Stack>
                                                        </ListItem>
                                                        {cIdx < collabs.length - 1 && (
                                                            <Divider
                                                                sx={{
                                                                    borderColor:
                                                                        "rgba(227, 227, 227, 0.35)",
                                                                }}
                                                            />
                                                        )}
                                                    </React.Fragment>
                                                );
                                            })}
                                        </List>
                                    </Box>
                                </Collapse>
                            )}
                        </Card>
                    </Fade>
                );
            })}
        </Stack>
    );
}
